import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../../../../context/AuthContext";

const initialForm = {
  name: "",
  section_uuid: "",
};

export default function StoreCategoryFormModal({
  open,
  onClose,
  category,
  reload,
}) {
  const { authFetch } = useAuth();

  const [form, setForm] = useState(initialForm);
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });

  useEffect(() => {
    if (!open) {
      setForm(initialForm);
      setBanner({ type: "", message: "" });
      return;
    }

    if (category) {
      setForm({
        name: category.name || "",
        section_uuid: category.section_uuid || "",
      });
    } else {
      setForm(initialForm);
    }

    setBanner({ type: "", message: "" });
  }, [open, category]);

  useEffect(() => {
    if (!open) return;

    const loadSections = async () => {
      setLoadingSections(true);

      try {
        const response = await authFetch("/api/store/sections/", {
          method: "GET",
        });

        const data = await response.json().catch(() => []);

        if (!response.ok) {
          setSections([]);
          setBanner({
            type: "error",
            message: data?.error || "No se pudieron cargar las secciones",
          });
          return;
        }

        setSections(Array.isArray(data) ? data : []);
      } catch {
        setSections([]);
        setBanner({
          type: "error",
          message: "Ocurrió un error al cargar las secciones",
        });
      } finally {
        setLoadingSections(false);
      }
    };

    loadSections();
  }, [open, authFetch]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (banner.message) {
      setBanner({ type: "", message: "" });
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setBanner({
        type: "error",
        message: "Ingresa el nombre de la categoría",
      });
      return;
    }

    if (!form.section_uuid) {
      setBanner({
        type: "error",
        message: "Selecciona una sección",
      });
      return;
    }

    const isEditing = Boolean(category?.uuid);

    const url = isEditing
      ? `/api/store/categories/${category.uuid}/update/`
      : "/api/store/categories/create/";

    const method = isEditing ? "PATCH" : "POST";

    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch(url, {
        method,
        body: JSON.stringify({
          name: form.name.trim(),
          section_uuid: form.section_uuid,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudo guardar la categoría",
        });
        return;
      }

      await reload();
      onClose();
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al guardar la categoría",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "18px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: 18,
          fontWeight: 700,
          color: "#0f172a",
          pb: 1,
        }}
      >
        {category ? "Editar categoría" : "Nueva categoría"}
      </DialogTitle>

      <DialogContent sx={{ pt: "8px !important" }}>
        <Stack spacing={1.4}>
          {banner.message && (
            <Alert
              severity={banner.type}
              sx={{
                borderRadius: "12px",
              }}
            >
              {banner.message}
            </Alert>
          )}

          <TextField
            label="Nombre"
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: "#ffffff",
              },
            }}
          />

          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={form.section_uuid}
              onChange={handleChange("section_uuid")}
              disabled={loadingSections}
              sx={{
                borderRadius: "10px",
                bgcolor: "#ffffff",
              }}
            >
              <MenuItem value="">
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: 14,
                  }}
                >
                  {loadingSections ? "Cargando secciones..." : "Selecciona una sección"}
                </Typography>
              </MenuItem>

              {sections.map((section) => (
                <MenuItem key={section.uuid} value={section.uuid}>
                  {section.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.2, pt: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{
            height: 36,
            px: 1.4,
            borderRadius: "9px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12.8,
            color: "#334155",
            bgcolor: "#ffffff",
            borderColor: "rgba(51,65,85,0.18)",
            "&:hover": {
              borderColor: "rgba(51,65,85,0.28)",
              bgcolor: "#f8fafc",
            },
          }}
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            height: 36,
            px: 1.4,
            borderRadius: "9px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12.8,
            bgcolor: "#0f172a",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#111827",
              boxShadow: "none",
            },
          }}
        >
          {loading ? "Guardando..." : category ? "Guardar cambios" : "Crear categoría"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}